import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Parameter, ParameterGroup as ParameterGroupType } from '../../types/SetpointManagerTypes';
import ParameterTable from '../ParameterTable/ParameterTable';
import { RootState } from '../../store';
import './parameter-group.scss';
import { Filters, SortingRule } from 'react-table';
import Helpers from '../../utilities/Helpers';

interface ParameterGroupProps {
  group: ParameterGroupType;
  onToggleExpand: (groupId: string) => void;
  showBackup: boolean;
  updateSortData: (group: string, sortData: SortingRule<Parameter>[]) => void;
  sortData: SortingRule<Parameter>[];
  updateFilters: (group: string, filters: Filters<Parameter>) => void;
  filters: Filters<Parameter>;
  rPermission: { [key: string]: boolean };
}

const ParameterGroup: React.FC<ParameterGroupProps> = ({
  group,
  onToggleExpand,
  showBackup,
  updateSortData,
  sortData,
  updateFilters,
  filters,
  rPermission,
}) => {
  const [filteredCount, setFilteredCount] = useState<number>(group.setpoints.length);
  const [filteredValues, setFilteredValues] = useState<Parameter[]>(group.setpoints);
  const [isGlobalFiltered, setIsGlobalFiltered] = useState<boolean>(false);
  const globalFilterValue = useSelector((state: RootState) => state.globalFilter.value);

  const handleToggle = () => {
    onToggleExpand(group.setpointGroup);
  };

  useEffect(() => {
    // Filter the setpoints based on the global filter value
    if (globalFilterValue) {
      const filter = globalFilterValue.toLowerCase();
      const filteredSetpoints = group.setpoints.filter((param) => {
        return [param.currentValue, param.description, param.parameter, param.uom]
          .map((attr) => (attr !== undefined && attr !== null ? String(attr).toLowerCase() : ''))
          .some((val) => val.includes(filter));
      });
      setFilteredValues(filteredSetpoints);
      setFilteredCount(filteredSetpoints.length);
      setIsGlobalFiltered(true);

      if (group.isExpanded === false && filteredSetpoints.length) {
        onToggleExpand(group.setpointGroup);
      }
    } else {
      setFilteredValues(group.setpoints);
      setFilteredCount(group.setpoints.length);
      setIsGlobalFiltered(false);
    }
  }, [globalFilterValue, group.setpoints]);

  return (
    <article className={`${filteredCount === 0 && isGlobalFiltered ? 'hide-group ' : ''}parameter-group`}>
      {/* Header for the parameter group */}
      <header
        className={`parameter-group__header ${group.isExpanded ? 'parameter-group__header--expanded' : ''}`}
        onClick={handleToggle}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-expanded={group.isExpanded}
        aria-controls={`group-content-${group.setpointGroup}`}
      >
        <div className='parameter-group__icon-container'>
          <div
            className={`parameter-group__expand-icon ${group.isExpanded ? 'parameter-group__expand-icon--expanded' : ''}`}
          >
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M4 6L8 10L12 6' stroke='#f7f9f9' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </div>
        </div>

        <div className='parameter-group__title-container'>
          <h2 className='parameter-group__title'>{Helpers.toSentenceCase(group.setpointGroupName)}</h2>
        </div>

        <div className='parameter-group__parameter-count-container'>
          <div className='parameter-group__parameter-count-wrapper'>
            <div className='parameter-group__parameter-count-badge'>
              <span className='parameter-group__parameter-count-text'>{filteredCount} parameters</span>
            </div>
            {/* <div className='parameter-group__parameter-count-badge' style={{ marginLeft: 8 }}>
              <span className='parameter-group__parameter-count-text'>{filteredCount} shown</span>
            </div> */}
          </div>
        </div>
      </header>

      {group.isExpanded && group.setpoints.length > 0 && (
        <div id={`group-content-${group.setpointGroup}`} className='parameter-table-scroll'>
          {/* Debug log for re-render */}
          <ParameterTable
            key={group.setpoints.map((p) => `${p.parameter}:${p.currentValue ?? ''}`).join(',')}
            parameters={filteredValues}
            showBackup={showBackup}
            updateSortData={(data) => updateSortData(group.setpointGroupName, data)}
            sortData={sortData || []}
            updateFilters={(data) => updateFilters(group.setpointGroupName, data)}
            filters={filters || []}
            onFilteredCountChange={(count) => {
              setFilteredCount(count);
            }}
            rPermission={rPermission}
          />
          {filteredCount === 0 && (
            <div className='parameter-group__no-results'>
              No records found for the applied filter criteria
            </div>
          )}
        </div>
      )}
    </article>

  );
};

export default ParameterGroup;
